/**
 * Espejo CLIENTE del RBAC de organización (`auth/org_rbac.rs`). Solo para UX:
 * decide qué controles de edición MOSTRAR. La autoridad real es el write-path
 * (`db/sync.rs`), que rechaza-y-loguea lo no autorizado — el cliente jamás es la
 * autoridad. Mantener en paridad con el mapa de capacidades de Rust.
 */

/** Capacidades del plano de organización (subconjunto que usa la UI). */
export type OrgCapability =
  | "ManageOrgSettings"
  | "ManageCampuses"
  | "ManageMembers"
  | "ManageAcademics"
  | "ManageStudents"
  | "ViewCampusData";

/**
 * TODAS las capacidades del plano de organización, en orden de presentación.
 *
 * Fuente ÚNICA para cualquier pantalla que EXPLIQUE lo que un rol permite
 * (§M1 «Mis perfiles»): recorrer esta lista y preguntar a [`orgRoleGrants`] es
 * lo que impide que la explicación derive del enforcement. El `satisfies`
 * fuerza la exhaustividad en compilación — añadir una `OrgCapability` sin
 * añadirla aquí no compila.
 */
export const ORG_CAPABILITIES = [
  "ManageOrgSettings",
  "ManageCampuses",
  "ManageMembers",
  "ManageAcademics",
  "ManageStudents",
  "ViewCampusData",
] as const satisfies readonly OrgCapability[];

/** Comprobación de exhaustividad: toda `OrgCapability` está en la lista. */
type _AllCapabilitiesListed =
  Exclude<OrgCapability, (typeof ORG_CAPABILITIES)[number]> extends never
    ? true
    : ["falta una OrgCapability en ORG_CAPABILITIES"];
const _capabilitiesAreExhaustive: _AllCapabilitiesListed = true;
void _capabilitiesAreExhaustive;

/** Todos los roles canónicos de organización, de mayor a menor privilegio. */
export const ORG_ROLES = [
  "owner",
  "org_admin",
  "campus_admin",
  "staff",
  "teacher",
  "guardian",
  "student",
] as const satisfies readonly OrgRole[];

/** Rol canónico de `member.role` (con alias legado `admin` → org_admin). */
export type OrgRole =
  | "owner"
  | "org_admin"
  | "campus_admin"
  | "teacher"
  | "staff"
  | "student"
  | "guardian";

/** Normaliza un `member.role` crudo al rol canónico (desconocido → student). */
export function normalizeOrgRole(role: string | null | undefined): OrgRole {
  switch (role) {
    case "owner": {
      return "owner";
    }
    case "org_admin":
    case "admin": {
      return "org_admin";
    }
    case "campus_admin": {
      return "campus_admin";
    }
    case "teacher": {
      return "teacher";
    }
    case "staff": {
      return "staff";
    }
    case "guardian": {
      return "guardian";
    }
    default: {
      return "student";
    }
  }
}

/** ¿El rol concede la capacidad? Paridad con `org_role_grants` (Rust). */
export function orgRoleGrants(cap: OrgCapability, role: OrgRole): boolean {
  switch (role) {
    case "owner":
    case "org_admin": {
      return true;
    }
    case "campus_admin": {
      return (
        cap === "ManageCampuses" ||
        cap === "ManageStudents" ||
        cap === "ManageAcademics" ||
        cap === "ViewCampusData"
      );
    }
    case "staff": {
      return cap === "ManageStudents" || cap === "ViewCampusData";
    }
    case "teacher": {
      return cap === "ViewCampusData";
    }
    default: {
      return false;
    }
  }
}

/** Conveniencia: ¿el rol (texto) concede la capacidad? */
export function roleHasCapability(role: string | null | undefined, cap: OrgCapability): boolean {
  return orgRoleGrants(cap, normalizeOrgRole(role));
}

/**
 * D3 · ¿el rol puede ver el CORREO de los demás miembros de la organización?
 * Espejo de `org_rbac::can_view_member_emails` (Rust). **Solo UX** —quién ve el
 * correo lo decide el servidor al construir la respuesta de `listMembers`; aquí
 * sirve para no pintar una columna «Correo» que llegaría siempre vacía.
 */
export function canViewMemberEmails(role: string | null | undefined): boolean {
  return roleHasCapability(role, "ManageMembers") || roleHasCapability(role, "ViewCampusData");
}
